import * as React from 'react';
import styled from '@emotion/styled';
import PopperJS from 'popper.js';
import PropTypes from 'prop-types';
import withTheme from '../utils/withTheme';
import { Portal } from '..';
import { DefaultProps, handleDefaultStyle } from '../utils';
// import { elementAcceptingRefs } from '../utils/customPropTypes';
import { setRef, useForkRef } from '../utils/ref';
// import usePopper from '../utils/usePopper';
import { useEffect } from '../utils/helper';

const renderComponent = (component = 'div') => {
	if (!styled[component]) {
		component = 'div';
	}
	return styled[component]`
		position: absolute;
		padding: 8px;
		background: #333;
		color: white;
		box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.3);
		text-align: center;
		font-size: 16px;
		font-weight: 400;
		${handleDefaultStyle}
	`;
};

const Tooltip = withTheme(
	React.forwardRef(function Tooltip(
		{
			children,
			delay,
			content,
			placement,
			containerPadding,
			arrow,
			defaultShow,
			...ComponentProp
		},
		ref
	) {
		const Component = renderComponent('div');
		const [visible, setVisibliltyState] = React.useState(defaultShow || false);
		// const [toolTipElement, setTooltipElement] = React.useState(null);
		const allChildRef = React.useRef([]);
		const selectedChildRef = React.useRef(0);
		const toolTipRef = React.useRef();

		// handle children
		let tooltipChild = children;
		let isChildrenValidReactComponent = true;

		if (!Array.isArray(children)) {
			isChildrenValidReactComponent = React.isValidElement(children);
			tooltipChild = [children];
		}

		// handle child Refs
		const handleRef = function(childRef) {
			// eslint-disable-next-line react/no-this-in-sfc
			const arrayIndex = this.index;
			const myCustomRef = React.createRef();
			setRef(myCustomRef, childRef);
			const temp = (allChildRef.current || []).slice();
			temp[arrayIndex] = myCustomRef;
			allChildRef.current = temp;
		};

		const tooltipContent = typeof content === 'function' ? content() : content;
		const forkRef = useForkRef(toolTipRef, ref);

		const [toolTipConfig, setToolTipConfig] = React.useState(false);
		const mountRef = React.useRef(false);

		const showToolTip = React.useCallback(
			(toolTipElement) => {
				const refElem = allChildRef.current[selectedChildRef.current]
					? allChildRef.current[selectedChildRef.current].current
					: null;
				if (refElem !== null && toolTipElement !== null) {
					if (mountRef.current === true) return false;
					mountRef.current = true;
					let popperPlacement =
						typeof placement === 'string' ? placement : 'right';
					const tPlacement = refElem.getAttribute('tooltipplacement');
					if (tPlacement && typeof tPlacement === 'string') {
						popperPlacement = tPlacement;
					}
					// eslint-disable-next-line no-new
					new PopperJS(refElem, toolTipElement, {
						placement: popperPlacement,
						enableEvents: visible,
						modifiers: {
							preventOverflow: {
								padding: containerPadding || 5,
							},
							arrow: {
								enabled: true,
								element: toolTipElement,
							},
						},
						onCreate: setToolTipConfig,
						onUpdate: setToolTipConfig,
					});
				}
				return null;
			},
			[placement, visible, containerPadding]
		);

		const handleToolTipRef = React.useCallback((node) => {
			if (node !== null) {
				forkRef(node);
				showToolTip(node);
			}
		}, []);

		useEffect(() => {
			if (!visible) {
				mountRef.current = false;
			}
		}, [visible]);

		const showToolTipState = (index, state = true) => {
			let show = 0;
			let hide = 0;
			if (typeof delay === 'number' && delay > 0) {
				show = delay;
				hide = delay;
			} else if (typeof delay === 'object') {
				if (typeof delay.show === 'number' && delay.show > 0) {
					show = delay.show;
				}
				if (typeof delay.hide === 'number' && delay.hide > 0) {
					hide = delay.hide;
				}
			}
			selectedChildRef.current = index;
			if (
				(state === false || (state === 'toggle' && visible === true)) &&
				hide > 0
			) {
				setTimeout(() => {
					setVisibliltyState(false);
				}, hide);
			} else if (show > 0) {
				setTimeout(() => {
					setVisibliltyState(true);
				}, show);
			} else if (state === 'toggle') {
				setVisibliltyState((current) => !current);
			} else {
				setVisibliltyState(state);
			}
		};

		const handleChildProps = (parentProps, childProps, index) => {
			let { trigger } = parentProps;
			if (typeof trigger === 'string') {
				trigger = [trigger];
			}
			if (!Array.isArray(trigger)) {
				trigger = ['hover'];
			}
			const hoverTrigger = (trigger || []).indexOf('hover') !== -1;
			const clickTrigger = (trigger || []).indexOf('click') !== -1;
			const focusTrigger = (trigger || []).indexOf('focus') !== -1;
			return {
				onMouseOver: (event, ...args) => {
					if (hoverTrigger) {
						showToolTipState(index);
					}
					if (typeof childProps.onMouseOver === 'function') {
						childProps.onMouseOver(event, ...args);
					}
				},
				onMouseOut: (event, ...args) => {
					if (hoverTrigger && !clickTrigger) showToolTipState(index, false);
					if (typeof childProps.onMouseOut === 'function') {
						childProps.onMouseOut(event, ...args);
					}
				},
				onClick: (event, ...args) => {
					if (clickTrigger) {
						showToolTipState(index, 'toggle');
					}
					if (typeof childProps.onClick === 'function') {
						childProps.onClick(event, ...args);
					}
				},
				onFocus: (event, ...args) => {
					if (focusTrigger) {
						showToolTipState(index);
					}
					if (typeof childProps.onFocus === 'function') {
						childProps.onFocus(event, ...args);
					}
				},
				onBlur: (event, ...args) => {
					if (focusTrigger && !clickTrigger) showToolTipState(index, false);
					if (typeof childProps.onBlur === 'function') {
						childProps.onBlur(event, ...args);
					}
				},
			};
		};

		const renderToolTip = React.useCallback(() => {
			if (visible) {
				console.log(toolTipConfig);
				return (
					<Portal>
						<Component
							{...ComponentProp}
							role='tooltip'
							ref={handleToolTipRef}
							data-testid='TooltipComponent'
							style={{
								...(ComponentProp.style || {}),
								position: 'fixed',
								top: 0,
								left: 0,
								zIndex: 1,
								...toolTipConfig.styles,
							}}
						>
							{tooltipContent}
						</Component>
					</Portal>
				);
			}
			return null;
		}, [ComponentProp, visible, toolTipConfig]);

		if (children) {
			return (
				<>
					{isChildrenValidReactComponent ? (
						React.Children.map(tooltipChild, (child, index) =>
							React.cloneElement(child, {
								ref: handleRef.bind({ ...child, index }),
								...handleChildProps(ComponentProp, child.props, index),
							})
						)
					) : (
						<span
							ref={handleRef.bind({ index: 0 })}
							{...handleChildProps(ComponentProp, {}, 0)}
						>
							{children}
						</span>
					)}
					{renderToolTip()}
				</>
			);
		}
		return null;
	})
);

Tooltip.propTypes = {
	/**
	 * Tooltip reference element.
	 */
	children: PropTypes.node.isRequired,
	/**
	 * If `true`, adds an arrow to the tooltip.
	 */
	arrow: PropTypes.bool,
	/**
	 * Specify which action or actions trigger Tooltip visibility
	 *
	 * @type {'hover' | 'click' |'focus' | Array<'hover' | 'click' |'focus'>}
	 */
	trigger: PropTypes.oneOfType([
		PropTypes.oneOf(['hover', 'click', 'focus']),
		PropTypes.arrayOf(PropTypes.oneOf(['hover', 'click', 'focus'])),
	]),
	/**
	 * A millisecond delay amount to show and hide the Overlay once triggered
	 */
	delay: PropTypes.oneOfType([
		PropTypes.number,
		PropTypes.shape({
			show: PropTypes.number,
			hide: PropTypes.number,
		}),
	]),
	/**
	 * Tooltip Content, that will display
	 */
	content: PropTypes.node.isRequired,
	/**
	 * The initial visibility state of the Overlay. For more nuanced visibility
	 * control, consider using the Overlay component directly.
	 */
	defaultShow: PropTypes.bool,
	/**
	 * Sets the direction the Tooltip is positioned towards.
	 *
	 */
	placement: PropTypes.oneOf([
		'auto-start',
		'auto',
		'auto-end',
		'top-start',
		'top',
		'top-end',
		'right-start',
		'right',
		'right-end',
		'bottom-end',
		'bottom',
		'bottom-start',
		'left-end',
		'left',
		'left-start',
	]),
	/**
	 * Style class of the Tooltip
	 */
	className: PropTypes.string,
	/**
	 * ID of the Tooltip
	 */
	id: PropTypes.string,
	/**
	 * trigger function when ToolTip hide
	 */
	onHide: PropTypes.func,
	/**
	 * trigger function when ToolTip show
	 */
	onShow: PropTypes.func,
	/**
	 * Control how much space there is between the edge of the boundary element and tooltip.
	 */
	containerPadding: PropTypes.number,
	...DefaultProps,
};

Tooltip.defaultProps = {
	placement: 'top',
	trigger: ['hover', 'focus'],
	containerPadding: 5,
};

Tooltip.displayName = 'NbTooltip';

export default Tooltip;
